import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';

export interface SegmentPerformanceData {
    segment: string;
    call_count: number;
    resolution_rate: number;
    satisfaction_rate: number;
    avg_duration: number;
    performance_rate: number;
    pain_points_rate: number;
    reiteration_rate: number;
}

interface UseSegmentsPerformanceParams {
    projectId: number | null;
    startDate?: Date | null;
    endDate?: Date | null;
    enabled?: boolean;
}

export const useSegmentsPerformance = ({
    projectId,
    startDate,
    endDate,
    enabled = true,
}: UseSegmentsPerformanceParams) => {
    return useQuery({
        queryKey: ['segments-performance', projectId, startDate, endDate],
        queryFn: async () => {
            if (!projectId) {
                throw new Error('Project ID is required');
            }

            const { data, error } = await supabase.rpc('get_segments_performance', {
                p_project_id: projectId,
                p_start_date: startDate?.toISOString() || null,
                p_end_date: endDate?.toISOString() || null,
            });

            if (error) {
                console.error('Error fetching segments performance:', error);
                throw error;
            }

            return (data || []).map((item: any) => ({
                segment: item.result_segment,
                call_count: item.result_call_count,
                resolution_rate: item.result_resolution_rate,
                satisfaction_rate: item.result_satisfaction_rate,
                avg_duration: item.result_avg_duration,
                performance_rate: item.result_performance_rate,
                pain_points_rate: item.result_pain_points_rate,
                conversion_rate: item.result_conversion_rate,
                reiteration_rate: item.result_reiteration_rate
            })) as SegmentPerformanceData[];
        },
        enabled: !!projectId && enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};
